/**
 * Obfuscates a string using XOR cipher (Vigenère) + Base64URL
 * so that users cannot directly read or tamper with the payload.
 *
 * Uses base64url so the output is URL-safe without percent-encoding:
 *
 * @param value The string to encode.
 * @param key The key used for encoding/decoding.
 * @returns The encoded preset string.
 */
export function encodeString(value: string, key: string): string {
    const xored = value.split('').map((char, i) =>
        String.fromCharCode(char.charCodeAt(0) ^ key.charCodeAt(i % key.length)),
    ).join('');
    return btoa(xored).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * De-obfuscates a string using XOR cipher (Vigenère) + Base64
 * to retrieve the original value.
 *
 * Tolerant decoder: accepts both base64url (new) and plain base64 (legacy URLs),
 * and recovers from `+` being decoded as space via URL transport.
 *
 * TODO: as soon as the harder-fruehlings-angebot and harder-lunch-billett-urls are not used anymore,
 *       we can drop the support for legacy URLs that were not base64url-encoded.
 *
 * @param encoded The encoded string.
 * @param key The key used for encoding/decoding.
 * @returns The decoded string.
 */
export function decodeString(encoded: string, key: string): string {
    // Normalize: base64url → base64, recover `+` that was decoded as space by the query-string parser.
    let b64 = encoded.replace(/-/g, '+').replace(/_/g, '/').replace(/ /g, '+');
    // Re-add `=` padding to the next multiple of 4 (stripped by base64url).
    b64 += '='.repeat((4 - b64.length % 4) % 4);
    const xored = atob(b64);
    return xored.split('').map((char, i) =>
        String.fromCharCode(char.charCodeAt(0) ^ key.charCodeAt(i % key.length)),
    ).join('');
}
